import { useCallback, useMemo } from 'react';
import { AppId, FCX, ID, Tooltip, usePlatformContext } from '@datassential/platform-ui-lib';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { requestTrialByFeature } from '@actions/page';
import ComparisonBarChart from '@components/ComparisonBarChart/ComparisonBarChart';
import Table from '@components/new/Table/Table';
import { iTableConfigItem } from '@components/new/Table/Table.helpers';
import { useHomeMenuLaunchesAndLtoActivity } from '@apiHooks/useHomeMenuLaunchesAndLtoActivity';
import { useHomeCommonData } from '@apiHooks/useHomeCommonData';
import HomeSectionFooter from '@pages/Home/components/HomeSectionFooter';
import AccessLink from "@components/AccessLink/AccessLink";
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import { LR_APPLY_FILTERS_PATH, LR_CHAIN_NAME_PARAM } from '@pages/Home/constants';
import './MenuLaunchesAndLTO.scss';

const TABLE_HEAD_TOOLTIP_ID = 'menu-launches-lto-avg-scores-head';
const TABLE_ROW_COUNT = 2;
const TABLE_ROW_HEIGHT = 44;
const TABLE_HEAD_ROW_HEIGHT = 36;

const AVG_SCORES_TOOLTIP =
    'Score = composite score based on performance across all 6 key metrics (scores above 70 are considered good, while scores above 90 are considered great)';

function formatAverageScore(value: number | null | undefined) {
    if (value == null || !Number.isFinite(value)) {
        return '—';
    }
    return Math.round(value);
}

interface MenuLaunchesAndLTOProps {
    chainName: string;
    chainId: ID;
    comparePresetId: number;
    comparePresetName: string;
}

const MenuLaunchesAndLTO: FCX<MenuLaunchesAndLTOProps> = ({
    chainName,
    chainId,
    comparePresetId,
    comparePresetName,
}) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const { data: { lrCategories } } = useHomeCommonData();
    const { data: { chartData, averageScoreRows: scores }, isLoading } =
        useHomeMenuLaunchesAndLtoActivity(chainId, comparePresetId, lrCategories);

    const comparisonChart = useMemo(
        () => ({
            mainTitle: chainName,
            compareTitle: comparePresetName,
            mainTotal: chartData.mainTotal,
            compareTotal: chartData.compareTotal,
            values: chartData.values,
        }),
        [chartData, chainName, comparePresetName],
    );

    const tableConfig = useMemo((): iTableConfigItem[] => {
        const labelCol: iTableConfigItem = {
            renderTitle: (
                <>
                    <span className="MenuLaunchesAndLTO__head-title">
                        <span>Avg. scores</span>
                        <AiOutlineInfoCircle
                            className="MenuLaunchesAndLTO__info-icon"
                            data-tooltip-id={TABLE_HEAD_TOOLTIP_ID}
                        />
                    </span>
                    <Tooltip
                        id={TABLE_HEAD_TOOLTIP_ID}
                        place="top"
                        style={{ zIndex: 1, maxWidth: 256, fontSize: 14 }}
                    >
                        {AVG_SCORES_TOOLTIP}
                    </Tooltip>
                </>
            ),
            Value: ({ index }) => <>{index === 0 ? chainName : comparePresetName}</>,
            styles: {
                cell: {
                    flex: '0 0 120px',
                    padding: 0,
                    color: '#141932',
                    fontWeight: 500,
                },
                head: { height: `${TABLE_HEAD_ROW_HEIGHT}px` },
            },
        };

        const categoryCols: iTableConfigItem[] = scores.map((row) => ({
            Value: ({ index }) => (
                <>
                    {formatAverageScore(
                        index === 0 ? row.chainAverageScore : row.compareAverageScore,
                    )}
                </>
            ),
            styles: {
                cell: {
                    flex: '1 1 40px',
                    minWidth: 40,
                    padding: 0,
                    justifyContent: 'center',
                    color: '#141932',
                    fontWeight: 500,
                },
                head: { height: `${TABLE_HEAD_ROW_HEIGHT}px` },
            },
        }));

        return [labelCol, ...categoryCols];
    }, [chainName, comparePresetName, scores]);

    const launchesRatings = products[AppId.LaunchesAndRatings];

    const launchesUrl = useMemo(() => {
        if (!launchesRatings.url) return '';
        const q = new URLSearchParams({ [LR_CHAIN_NAME_PARAM]: chainName });
        return `${launchesRatings.url}${LR_APPLY_FILTERS_PATH}?${q}`;
    }, [chainName, launchesRatings.url]);

    const onRequestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('scores'));
    }, [dispatch]);

    const hasData = useMemo(
        () => chartData.values.length > 0 && scores.length > 0,
        [chartData.values, scores],
    );

    if (isLoading || !hasData) return null;

    return (
        <>
            <HomeSectionTitle style={{ paddingBottom: 10, paddingTop: 40 }}>
                Menu Launches & LTO Activity
            </HomeSectionTitle>
            <div className="MenuLaunchesAndLTO__header">
                <p>
                    Share of&nbsp;
                    <span className="MenuLaunchesAndLTO__header-chain-name">{chainName}</span>
                    &nbsp;Launches by Category vs Average Among {comparePresetName}
                </p>
                <p>past 12 months</p>
            </div>
            <div className="MenuLaunchesAndLTO__panel">
                <div className="MenuLaunchesAndLTO__scroll">
                    <ComparisonBarChart data={comparisonChart} />
                    <Table
                        isCopyButtonHidden
                        config={tableConfig}
                        rowsCount={TABLE_ROW_COUNT}
                        rowHeight={TABLE_ROW_HEIGHT}
                        theme="light"
                        hasInnerScroll={false}
                    />
                </div>
            </div>
            <HomeSectionFooter className="MenuLaunchesAndLTO__footer">
                <AccessLink
                    url={launchesUrl}
                    text={`More ${chainName} Launches`}
                    altText=""
                    hasAccess={!launchesRatings.isDisabled}
                    onContactUsClick={onRequestTrial}
                    testId=""
                />
            </HomeSectionFooter>
        </>
    );
};

export default MenuLaunchesAndLTO;
